import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CalendarPlus, ListOrdered, Mail, MessageCircle, Phone, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { WaitlistEntry } from "@/lib/db-service/service-waitlist";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";
import type { ServiceBooking } from "@/lib/db-service/service-bookings";
import { NewBookingDialog } from "@/components/services/NewBookingDialog";

export const Route = createFileRoute("/app/lista-espera")({
  component: Page,
  head: () => ({ meta: [{ title: "Lista de espera" }] }),
});

function formatWaitlistDate(value?: string | null): string {
  if (!value) return "—";
  const iso = value.includes("T") ? value : `${value.slice(0, 10)}T12:00:00`;
  try {
    return format(parseISO(iso), "EEE d MMM yyyy", { locale: es });
  } catch {
    return value.slice(0, 10);
  }
}

function formatCreatedAt(value: string): string {
  try {
    const iso = value.includes("T") ? value : `${value.slice(0, 10)}T12:00:00`;
    return format(parseISO(iso), "d MMM yyyy", { locale: es });
  } catch {
    return value.slice(0, 10);
  }
}

function whatsAppUrl(phone: string, message: string): string {
  const digits = phone.replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<WaitlistEntry | null>(null);
  const [assignEntry, setAssignEntry] = useState<WaitlistEntry | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [w, p, s] = await Promise.all([
        apiGetJson<{ items: WaitlistEntry[] }>("/api/service-admin?resource=waitlist", tenantId),
        apiGetJson<{ items: ServiceProfessional[] }>("/api/service-admin?resource=professionals", tenantId),
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
      ]);
      setEntries(w.items ?? []);
      setProfessionals(p.items ?? []);
      setServices(s.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [tenantId]);

  const serviceName = (id: string | null) => (id ? services.find((s) => s.id === id)?.name ?? "—" : "—");
  const professionalName = (id: string | null) => (id ? professionals.find((p) => p.id === id)?.name ?? "—" : "—");

  async function handleDelete(id: string) {
    if (!tenantId) return;
    try {
      await apiDelete(`/api/service-admin?resource=waitlist&id=${id}`, tenantId);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Eliminado de la lista");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar");
    }
  }

  function openAssign(entry: WaitlistEntry) {
    setSelected(null);
    setAssignEntry(entry);
    setBookingOpen(true);
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.customerName.toLowerCase().includes(q) ||
        e.customerEmail.toLowerCase().includes(q) ||
        serviceName(e.serviceId).toLowerCase().includes(q) ||
        professionalName(e.professionalId).toLowerCase().includes(q),
    );
  }, [entries, search, services, professionals]);

  const withPhone = entries.filter((e) => !!e.customerPhone?.trim()).length;

  const notifyMessage = (entry: WaitlistEntry) =>
    `¡Hola ${entry.customerName.split(" ")[0] ?? ""}! Se liberó un turno para ${serviceName(entry.serviceId)}. ¿Querés reservarlo?`;

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="lista-espera" />
      <CrudShellHeader
        title="Lista de espera"
        subtitle="Contactá clientes en espera y asignales un turno cuando haya disponibilidad"
        count={entries.length}
      />
      <div className="space-y-4 px-4 pb-8">
        <div className="grid gap-3 sm:grid-cols-2">
          <ClinicalStatCard label="En espera" value={entries.length} icon={ListOrdered} variant="primary" />
          <ClinicalStatCard
            label="Con teléfono"
            value={withPhone}
            hint="WhatsApp o llamada directa desde cada fila"
            icon={Phone}
            variant="success"
          />
        </div>

        <Input placeholder="Buscar por nombre, email, servicio…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <p className="text-xs text-muted-foreground">
          {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
        </p>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <ListOrdered className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">
              {entries.length === 0
                ? "La lista de espera está vacía. Los clientes se anotan desde la tienda online."
                : "Sin resultados para la búsqueda."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Servicio</TableHead>
                  <TableHead>Profesional</TableHead>
                  <TableHead>Fecha pref.</TableHead>
                  <TableHead>Anotado el</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer hover:bg-primary/5" onClick={() => setSelected(e)}>
                    <TableCell>
                      <div className="font-medium">{e.customerName}</div>
                      <div className="text-xs text-muted-foreground">{e.customerEmail}</div>
                    </TableCell>
                    <TableCell className="text-sm">{serviceName(e.serviceId)}</TableCell>
                    <TableCell className="text-sm">{professionalName(e.professionalId)}</TableCell>
                    <TableCell className="text-sm">{formatWaitlistDate(e.preferredDate)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatCreatedAt(e.createdAt)}</TableCell>
                    <TableCell onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        <Button size="sm" variant="default" className="h-8 gap-1" onClick={() => openAssign(e)}>
                          <CalendarPlus className="h-3.5 w-3.5" />
                          Asignar turno
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
                          <a href={`mailto:${e.customerEmail}`} title="Enviar email">
                            <Mail className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                        {e.customerPhone?.trim() ? (
                          <>
                            <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
                              <a href={whatsAppUrl(e.customerPhone, notifyMessage(e))} target="_blank" rel="noreferrer" title="WhatsApp">
                                <MessageCircle className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
                              <a href={`tel:${e.customerPhone}`} title="Llamar">
                                <Phone className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          </>
                        ) : null}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Eliminar">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar de la lista?</AlertDialogTitle>
                              <AlertDialogDescription>Se quitará a {e.customerName} de la lista de espera.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => void handleDelete(e.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  {selected.customerName}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-5 text-sm">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contacto</p>
                  <p>{selected.customerEmail}</p>
                  <p className="text-muted-foreground">{selected.customerPhone?.trim() || "Sin teléfono"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Interés</p>
                  <p>{serviceName(selected.serviceId)}</p>
                  <p className="text-muted-foreground">{professionalName(selected.professionalId) === "—" ? "Cualquier profesional" : professionalName(selected.professionalId)}</p>
                  <p className="text-muted-foreground">Fecha preferida: {formatWaitlistDate(selected.preferredDate)}</p>
                  <p className="text-xs text-muted-foreground">Anotado el {formatCreatedAt(selected.createdAt)}</p>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <Button className="w-full gap-2" onClick={() => openAssign(selected)}>
                    <CalendarPlus className="h-4 w-4" />
                    Asignar turno
                  </Button>
                  <Button variant="outline" className="w-full gap-2" asChild>
                    <a href={`/app/clientes?vista=b2c&detalle=${encodeURIComponent(selected.customerEmail)}`}>
                      Ver ficha de cliente
                    </a>
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" className="gap-2" asChild>
                      <a href={`mailto:${selected.customerEmail}`}>
                        <Mail className="h-4 w-4" /> Email
                      </a>
                    </Button>
                    {selected.customerPhone?.trim() ? (
                      <Button variant="outline" className="gap-2" asChild>
                        <a href={whatsAppUrl(selected.customerPhone, notifyMessage(selected))} target="_blank" rel="noreferrer">
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                      </Button>
                    ) : (
                      <Button variant="outline" disabled className="gap-2">
                        <MessageCircle className="h-4 w-4" /> Sin WhatsApp
                      </Button>
                    )}
                  </div>
                  <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => void handleDelete(selected.id)}>
                    Eliminar de la lista
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      {tenantId ? (
        <NewBookingDialog
          open={bookingOpen}
          onOpenChange={(open) => {
            setBookingOpen(open);
            if (!open) setAssignEntry(null);
          }}
          tenantId={tenantId}
          professionals={professionals}
          services={services}
          defaultProfessionalId={assignEntry?.professionalId ?? undefined}
          defaultServiceId={assignEntry?.serviceId ?? undefined}
          defaultDate={assignEntry?.preferredDate?.slice(0, 10)}
          defaultCustomerName={assignEntry?.customerName}
          defaultCustomerEmail={assignEntry?.customerEmail}
          defaultCustomerPhone={assignEntry?.customerPhone ?? undefined}
          defaultNotes={assignEntry ? `Desde lista de espera (${assignEntry.id})` : undefined}
          onCreated={(booking: ServiceBooking) => {
            if (assignEntry) void handleDelete(assignEntry.id);
            void load();
            void booking;
          }}
        />
      ) : null}
    </ClinicalPageShell>
  );
}
